/**
 * Foundation plugin that stops normal scrolling a the applied container
 * and adds classes synced with scroll inside of it
 *
 */

import { Foundation } from 'foundation-sites/js/foundation.core';

class ScrollAnimation {
    constructor(element, options) {
        this.$element = element;
        this.containerElem = this.$element[0];

        this.options = { ...ScrollAnimation.defaults, ...this.$element.data(), ...options };
        Foundation.registerPlugin(this, 'ScrollAnimation');

        this.scrollSteps = this.containerElem.querySelectorAll('.fact').length;

        this.mode = 'above';
        this.active = null;

        this.lastScrollY = ScrollAnimation.scrollY;
        this.lastScrollTime = Date.now();

        this.scrollSpaceElem = this.createScrollSpaceElem();

        this.ticking = false;
        window.addEventListener('scroll', this.eventHandler.bind(this), { passive: true });
        window.addEventListener('resize', this.eventHandler.bind(this), { passive: true });
        document.body.addEventListener('focusout', this.focusOutHandler.bind(this));
    }

    /**
     * Get the the vertical pixel position of the current viewport center
     * @returns {Number}
     */
    static get scrollY() {
        const viewportOffset = window.innerHeight / 2;
        const currentScrollY = window.scrollY || document.documentElement.scrollTop;
        const scrollY = currentScrollY + viewportOffset;

        return scrollY;
    }

    /**
     * Get the (virtual) height the container gets to scroll its content while the body is fixed
     * @returns {Number}
     */
    get scrollSpaceHeight() {
        return this.scrollSpaceElem.firstChild.offsetHeight * (this.scrollSteps - 1);
    }

    /**
     * Get the vertical position of the scrollSpace (start and end position)
     * @returns {{elementStart: number, elementEnd: number}}
     */
    get elementPosition() {
        const elemRect = this.containerElem.getBoundingClientRect();
        const wrapperRect = document.getElementById('body-wrapper').getBoundingClientRect();

        const elementStart = elemRect.top + elemRect.height / 2 - wrapperRect.top;

        const elementEnd = elementStart + this.scrollSpaceHeight;

        return {
            elementStart,
            elementEnd,
        };
    }

    createScrollSpaceElem() {
        const scrollSpaceElem = document.createElement('div');
        scrollSpaceElem.classList.add('scroll-animation-space');
        scrollSpaceElem.style.position = 'relative';
        scrollSpaceElem.style.pointerEvents = 'none';

        let newElem = null;
        for (let i = 0; i < this.scrollSteps; i += 1) {
            newElem = document.createElement('div');
            newElem.style.height = '90vh';
            newElem.style.position = 'relative';
            scrollSpaceElem.appendChild(newElem);
        }

        newElem.style.display = 'none';

        document.body.appendChild(scrollSpaceElem);

        return scrollSpaceElem;
    }

    eventHandler() {
        if (!this.ticking) {
            this.ticking = true;
            requestAnimationFrame(this.update.bind(this));
        }
    }

    update() {
        this.ticking = false;

        // prevent scroll updates while blur is active (as they would fail anyways)
        if (document.body.classList.contains('blur-active')) {
            return;
        }

        const { scrollY } = ScrollAnimation;

        let scrollPrediction = 0;
        if (Date.now() - this.lastScrollTime < 200) {
            scrollPrediction = scrollY - this.lastScrollY;
        }
        const nextScrollY = scrollY + scrollPrediction;

        this.lastScrollY = scrollY;
        this.lastScrollTime = Date.now();

        const { elementStart, elementEnd } = this.elementPosition;

        const animationScrollPx = Math.max(0, Math.min(this.scrollSpaceHeight, (scrollY - elementStart)));
        const animationScrollPercentage = (animationScrollPx * 100) / this.scrollSpaceHeight;

        if (
            (elementStart <= scrollY || elementStart <= nextScrollY)
            && (elementEnd >= scrollY || elementEnd >= nextScrollY)
        ) {
            // we are in the scroll area of the element
            this.activateInsideMode();

            if (animationScrollPx > 10 && animationScrollPx < this.scrollSpaceHeight - 10) {
                this.scrollSpaceElem.classList.add('snap');
            } else {
                this.scrollSpaceElem.classList.remove('snap');
            }
        } else if (elementStart > scrollY) {
            // element is beneath
            this.activateAboveMode();

            this.scrollSpaceElem.classList.remove('snap');
        } else {
            // element is above
            this.activateBeneathMode();

            this.scrollSpaceElem.classList.remove('snap');
        }

        this.setActive(animationScrollPercentage);
    }

    activateAboveMode() {
        if (this.mode === 'above') {
            return;
        }
        this.mode = 'above';

        this.scrollSpaceElem.lastChild.style.display = 'none';
        document.body.insertBefore(this.scrollSpaceElem, document.body.lastChild.nextSibling);

        document.body.style.height = null;
        this.resetFixed();
    }

    activateBeneathMode() {
        if (this.mode === 'beneath') {
            return;
        }
        this.mode = 'beneath';

        this.scrollSpaceElem.lastChild.style.display = 'none';
        document.body.insertBefore(this.scrollSpaceElem, document.body.firstChild);

        document.body.style.height = null;
        this.resetFixed();
    }

    activateInsideMode() {
        if (this.mode !== 'inside') {
            document.body.style.height = `${document.getElementById('body-wrapper').offsetHeight
                + this.scrollSpaceElem.offsetHeight}px`;

            const elemRect = this.containerElem.getBoundingClientRect();
            const wrapperRect = document.getElementById('body-wrapper').getBoundingClientRect();
            const wrapperOffset = elemRect.top + elemRect.height / 2 - wrapperRect.top - window.innerHeight / 2;

            document.getElementById('body-wrapper').style.top = `-${wrapperOffset}px`;
            document.getElementById('body-wrapper').style.position = 'fixed';
            document.getElementById('body-wrapper').style.left = '0';
            document.getElementById('body-wrapper').style.right = '0';

            this.scrollSpaceElem.style.top = `${wrapperOffset}px`;
            this.scrollSpaceElem.lastChild.style.display = 'block';

            document.documentElement.style.scrollPaddingTop = 'unset';
        }

        this.mode = 'inside';
    }

    resetFixed() {
        document.getElementById('body-wrapper').style.top = null;
        document.getElementById('body-wrapper').style.position = 'relative';
        document.getElementById('body-wrapper').style.left = null;
        document.getElementById('body-wrapper').style.right = null;

        this.scrollSpaceElem.style.top = null;

        document.documentElement.style.scrollPaddingTop = null;
    }

    setActive(percentage) {
        const activeFloor = percentage / (100 / (this.scrollSteps - 1));
        let active = Math.round(activeFloor);

        active = Math.min(Math.max(active, 0), this.scrollSteps - 1);

        if (this.active !== active) {
            const currentActiveElem = this.containerElem.querySelector('.fact.active');
            if (currentActiveElem) {
                currentActiveElem.classList.remove('active');
            }
            this.containerElem.querySelector(`.fact-${active}`).classList.add('active');

            const bgElem = this.containerElem.querySelector('.facts-background');
            bgElem.className = `facts-background state-${active + 1}`;

            this.active = active;
        }
    }

    /**
     * Handler for the focusOut event - calculates (new) mode after a focus change,
     * e.g. focus jumps from above the container beneath it (without a scroll)
     * @param {any} e - the focusOut event
     */
    focusOutHandler(e) {
        /* eslint-disable no-bitwise */
        if (!e.relatedTarget) {
            return;
        }

        // determine relative position using compareDocumentPosition
        const containerPos = this.containerElem.compareDocumentPosition(e.relatedTarget);
        let focusJump = false;

        if ((containerPos & Node.DOCUMENT_POSITION_PRECEDING)
            && this.mode !== 'above'
        ) {
            // Check if target is above container
            focusJump = true;
            this.activateAboveMode();
        } else if ((containerPos & Node.DOCUMENT_POSITION_FOLLOWING)
            && this.mode !== 'beneath'
        ) {
            // Check if target is below container
            focusJump = true;
            this.activateBeneathMode();
        }

        if (focusJump) {
            e.target.scrollIntoView({
                behavior: 'instant',
                block: 'center',
            });
        }
    }
}

/**
 * Default settings
 *
 */
ScrollAnimation.defaults = {
};

// only activate this plugin if the user is ok with animations in general
if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    Foundation.plugin(ScrollAnimation, 'ScrollAnimation');
}
